import { useMsal } from '@azure/msal-react';
import {
  Avatar,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';

import { getUserPhotoDataUrl } from '../api/graph';
import { FONT, TEXT } from '../theme/tokens';

// The signed-in operator, at the RIGHT of the top bar (FD-481).
//
// It used to sit at the foot of the navy rail, and it was unreadable there — a
// floating avatar beside a label nobody could see. That was a real defect, not
// only a placement preference, and the mechanism is worth keeping written down
// because it will recur the next time two Griffel classes are combined.
//
// THE BUG: the rail rendered `variant="topbar"`, whose dark-chrome override set
// the name to white — and the classes were combined with a TEMPLATE STRING
// (`${styles.displayName} ${styles.displayNameOnDark}`) instead of
// `mergeClasses`. Griffel's atomic classes all carry the same specificity, so
// the winner is whichever is declared later in the stylesheet, and the override
// was declared ABOVE the base. The dark-chrome rule therefore lost to the
// `#18181b` it existed to replace, and the operator's name rendered near-black
// on `#061B2C`.
//
// `mergeClasses` is what resolves Griffel conflicts by INTENT rather than by
// declaration order, and it is used for every combination in this file. Fixing
// it by reordering the style object would have fixed this instance and left the
// trap in place for the next one.
//
// Azure-portal placement is deliberate: far right, past the environment
// selector, avatar TRAILING the name. That is where the operators this console
// is aimed at already look, and the top bar is light, so the dark-chrome
// treatment has no caller and is gone rather than kept warm.

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    backgroundColor: 'transparent',
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '8px',
    paddingRight: '8px',
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    // Right-aligned, because the block sits at the end of the bar and its two
    // lines should agree with the edge they are nearest.
    textAlign: 'right',
    minWidth: 0,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  textBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    minWidth: 0,
    lineHeight: '1.25',
  },
  displayName: {
    fontFamily: FONT.display,
    fontSize: '12px',
    fontWeight: 600,
    color: TEXT.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  email: {
    fontFamily: FONT.display,
    fontSize: '11px',
    color: TEXT.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Below ~900px the two-line block crowds the search box; the avatar alone is
  // still a complete control because the menu carries the name and the email.
  textBlockResponsive: {
    display: 'none',
    '@media (min-width: 900px)': {
      display: 'flex',
    },
  },
  menuHeader: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: '1.3',
    paddingTop: '6px',
    paddingBottom: '8px',
    paddingLeft: '10px',
    paddingRight: '10px',
    marginBottom: '4px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
});

export default function UserProfile() {
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const displayName = account?.name ?? account?.username ?? 'Signed in';
  const email = account?.username ?? '';

  const { data: photoDataUrl } = useQuery<string | null>({
    queryKey: ['user-photo', account?.homeAccountId ?? 'anonymous'],
    queryFn: getUserPhotoDataUrl,
    // Profile photos don't change during a session. Never refetch after the
    // initial success; keep cached indefinitely (homeAccountId in the key
    // means sign-out + sign-in-as-different-user triggers a fresh fetch).
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: Boolean(account),
  });

  const handleLogout = () => {
    instance.logoutRedirect().catch((err) => {
      console.error('MSAL logout redirect failed', err);
    });
  };

  return (
    <Menu positioning={{ position: 'below', align: 'end' }}>
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.trigger}
          aria-label={`Signed in as ${displayName}`}
        >
          <span className={mergeClasses(styles.textBlock, styles.textBlockResponsive)}>
            <span className={styles.displayName}>{displayName}</span>
            {email && email !== displayName && (
              <span className={styles.email}>{email}</span>
            )}
          </span>
          <Avatar
            name={displayName}
            image={photoDataUrl ? { src: photoDataUrl } : undefined}
            size={32}
          />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {/* The trigger's text block is hidden on narrow viewports, so the menu
              always carries both. Which account you are signed in as is exactly
              the thing an operator checks before a destructive action. */}
          <div className={styles.menuHeader}>
            <span className={styles.displayName}>{displayName}</span>
            {email && <span className={styles.email}>{email}</span>}
          </div>
          <MenuItem icon={<LogOut size={16} strokeWidth={1.5} />} onClick={handleLogout}>
            Sign out
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
